import { useState } from "react";
import { Link } from "react-router";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { localPorCodigo, produtoPorCodigo } from "../../lib/leitura";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtNum } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Vazio } from "../../componentes/ui";

interface Contado { produto_id: string; codigo: string; nome: string; sku: string | null; quantidade: number }

/** Inventário cego de uma posição: bipa tudo o que tem nela, sem ver o saldo do sistema. */
export default function Inventario() {
  const [posicao, setPosicao] = useState<string | null>(null);
  const [contados, setContados] = useState<Contado[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Array<{ sku: string; antes: number; depois: number }> | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);

  function falhou(m: string) {
    sinalErro();
    setTremor((n) => n + 1);
    setErro(m);
  }

  async function leitura(codigo: string) {
    if (!posicao) {
      const local = await localPorCodigo(codigo);
      if (!local) return falhou("Local não cadastrado neste depósito");
      sinalOk();
      setLampejo((n) => n + 1);
      setPosicao(local.codigo);
      setResultado(null);
      return;
    }
    const p = await produtoPorCodigo(codigo);
    if (!p) return falhou("Código não cadastrado");
    const existente = contados.find((c) => c.produto_id === p.id);
    if (existente) {
      setContados(contados.map((c) => (c.produto_id === p.id ? { ...c, quantidade: c.quantidade + 1 } : c)));
    } else {
      setContados([...contados, { produto_id: p.id, codigo, nome: p.nome, sku: p.codigo, quantidade: 1 }]);
    }
    sinalOk();
    setLampejo((n) => n + 1);
  }

  async function concluir() {
    if (!posicao) return;
    setOcupado(true);
    try {
      const r = await rpc<{ ok: boolean; ajustes?: Array<{ sku: string; antes: number; depois: number }>; mensagem?: string }>(
        "wms_inventario_local",
        { p_codigo_local: posicao, p_contagem: contados.map((c) => ({ codigo: c.codigo, quantidade: c.quantidade })), p_idem: novaChave() },
      );
      setConfirmar(false);
      if (!r.ok) return falhou(r.mensagem ?? "Inventário recusado");
      sinalConcluido();
      setResultado(r.ajustes ?? []);
      setPosicao(null);
      setContados([]);
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  function ajustar(produtoId: string, delta: number) {
    setContados(contados.map((c) => (c.produto_id === produtoId ? { ...c, quantidade: Math.max(0, c.quantidade + delta) } : c)));
  }

  return (
    <div className="flex flex-col gap-3">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Contar posição</h1>
      {!posicao && !resultado && (
        <p className="text-aco-escuro">Conte uma posição agora: bipe a posição e cada unidade que está nela. Você não vê o saldo do sistema; ao concluir, o saldo fica igual ao que você contou.</p>
      )}

      {resultado && (
        <section className="rounded-lg border-2 border-ok bg-chapa p-4">
          <h2 className="font-sinal text-2xl font-bold text-ok">Inventário gravado</h2>
          {resultado.length
            ? <ul className="mt-2">{resultado.map((a) => <li key={a.sku}>{a.sku}: {fmtNum(a.antes)} para <strong>{fmtNum(a.depois)}</strong></li>)}</ul>
            : <p className="mt-2">Sem diferença: o saldo do sistema já batia.</p>}
        </section>
      )}

      {posicao && (
        <section className="rounded-lg border-2 border-tinta bg-chapa">
          <div className="flex items-center justify-between border-b-2 border-aco px-4 py-3">
            <span className="font-sinal text-5xl font-bold">{posicao}</span>
            <Botao variante="fantasma" onClick={() => { setPosicao(null); setContados([]); }}>Trocar posição</Botao>
          </div>
          {contados.length ? (
            <ul className="divide-y divide-aco">
              {contados.map((c) => (
                <li key={c.produto_id} className="flex items-center justify-between gap-2 px-4 py-2">
                  <div><p className="font-semibold">{c.nome}</p><p className="text-sm text-aco-escuro">{c.sku}</p></div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => ajustar(c.produto_id, -1)} className="h-11 w-11 rounded-md border-2 border-tinta text-xl font-bold" aria-label="Menos um">-</button>
                    <span className="w-14 text-center font-sinal text-3xl font-bold">{fmtNum(c.quantidade)}</span>
                    <button type="button" onClick={() => ajustar(c.produto_id, 1)} className="h-11 w-11 rounded-md border-2 border-tinta text-xl font-bold" aria-label="Mais um">+</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : <div className="p-4"><Vazio>Bipe cada unidade que está na posição. Posição vazia: conclua sem bipar nada.</Vazio></div>}
        </section>
      )}

      <LeituraCodigo rotulo={posicao ? "Bipe os produtos da posição" : "Bipe a posição"} onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />

      {posicao && <Botao variante="confirmar" grande onClick={() => setConfirmar(true)}>Concluir contagem</Botao>}
      <Link to="/estoque" className="text-center font-semibold underline">Voltar</Link>

      <Confirmar aberto={confirmar} titulo={`Gravar a contagem de ${posicao}?`} rotuloConfirmar="Gravar inventário" ocupado={ocupado}
        onConfirmar={() => void concluir()} onCancelar={() => setConfirmar(false)}>
        O que não foi bipado fica com saldo zero nesta posição.
      </Confirmar>
    </div>
  );
}
